import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Footer from '../../components/footer/Footer';
import { setSelectionRevealState } from '../../store/actions';
import MathBox from '../../components/mathBox/MathBox';
import useLargeBreakpoint from '../../hooks/useLargeBreakpoint';
import NetPriceBox from './netPriceBox/NetPriceBox';
import PaymentEstimatorModal from './paymentEstimator/PaymentEstimatorModal';
import Inventory from '../../components/inventory/Inventory';
import KeyAttributes from '../../components/keyAttributes/KeyAttributes';
import SummaryFinanceContent from './SummaryFinanceContent';
import GMWCContainer from './GMWCContainer';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { getQueryStringValue } from '../../common/routing/queryStringHelpers';
import { Button } from '../../../core/controls/button/Button';
import SummaryInteriorCGI from './SummaryInteriorCgi';
import SummaryExteriorCgi from './SummaryExteriorCgi';
import { SvgIcon } from '../../../core/icons';
import fetchStandardFeatures from '../../api/fetchStandardFeatures';
import StandardFeatures from '../../components/standardFeatures/StandardFeatures';
import { Translate } from 'core/translate';
import Chat from '../../components/chat/Chat';
import { TranslateHTML } from '../../../core/translate';
import withSaveBuild from '../../common/saveBuild/SaveBuild';
import MoreViewsButton from '../../components/moreViewsButton/MoreViewsButton';
import SendLink from './sendLink/SendLink';
import NextStepsLinks from '../../components/nextStepsLinks/NextStepsLinks';
import FeaturedContent from './featuredContent/FeaturedContent';
import { combineClassNames } from '../../../core/css';
import { get, template, find } from 'lodash';
import {
  computeDefaultAngle,
  computeImageSize,
} from '../../common/cgi/cgiHelpers';
import LegalDisclosure from '../../components/legalDisclosure/LegalDisclosure';
import { EXTERIOR, INTERIOR, SUMMARY } from '../../common/steps';
import { useModelInfo } from '../../common/ModelInfoContext';
import SubmitOrder from './SubmitOrder';
import ToastModal, {
  ToastModalContent,
} from '../../main/toastModal/ToastModal';
import { fetchInventory } from '../../api/fetchInventory';
import { withNotifications } from '../../common/notifications/NotificationsContext';
import { onOpenDisclosure } from '../../common/notifications/NotificationsProvider';

export const SummaryInner = ({ children, className, condition }) => {
  return condition ? <div className={className}>{children}</div> : children;
};

export const PrintButton = ({ analyticsKey, className }) => (
  <Button
    className={combineClassNames(
      'stat-button-link',
      'summary__links__button',
      className
    )}
    onClick={() => {
      window.print();
    }}
    data-dtm={analyticsKey}>
    <SvgIcon name="print" className="button__icon--left summary-button__icon" />
    <TranslateHTML as="span" value="component.summary.print" />
  </Button>
);

const Summary = (props) => {
  const {
    pricing,
    appResources,
    config,
    isSelectionRevealed,
    modelInfo,
    onLoad,
    params,
    onEditLocation,
    saveBuild,
    removeBuild,
    currentBuildSavedKey,
    inventoryPageUrlTemplate,
    breakpoint,
    cgiSize,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    disclaimers,
    country,
    moreViewsPath,
    reservation,
    notifications,
  } = props;
  const ANALYTICS_KEY = 'summary';
  const printAnalyticsKey = 'right rail';

  const isAtLeastLarge = useLargeBreakpoint();
  const navigate = useNavigate();
  const { paymentEstimatorComponent } = appResources.display;
  const { processSteps } = appResources;
  const summaryStep = find(processSteps, { key: SUMMARY });
  const { isEvMode, hasTransactionId, isCanada } = useModelInfo();

  const [isLoadingStandardFeatures, setIsLoadingStandardFeatures] = useState(
    true
  );
  const [standardFeatures, setStandardFeatures] = useState(null);
  const [showSavedBuildsToast, setShowSavedBuildsToast] = useState(false);

  const ctas = get(config.summary, 'callsToAction');

  const standardEquipmentTitle = `${
    config.bodyType.formattedConfig && !isEvMode
      ? config.bodyType.formattedConfig
      : ''
  } ${config.style.trimName} ${config.driveType.label}`;

  const nextStepsRef = React.createRef();

  const privateOrderingMessageLabel =
    appResources.display.privateOrderingMessage;

  const hasFeeDisclaimers = pricing.incentives?.lines?.some(
    (line) => Array.isArray(line.disclaimers) && line.disclaimers.length > 0
  );

  const evStaticDisclosure = appResources.evMode;
  // Get EV Government Incentive disclaimers for programId: 273 - JIRA:https://gm-sdv.atlassian.net/browse/SDPMVB-298
  const evGovtIncentive = pricing.incentives?.lines?.filter(
    (incentive) => incentive.programId === '273'
  );

  const evGovtIncentiveDisclosure =
    evGovtIncentive
      ?.flatMap((line) => line.disclaimers.map((d) => `${line.program} - ${d}`))
      ?.join('<br>') || '';

  useEffect(() => {
    if (!isSelectionRevealed) {
      onLoad();
    }
  }, []);

  useEffect(() => {
    const nextStepsEl = nextStepsRef.current;

    const makeNextStepsScrollable = () => {
      const nextStepsDimensions = nextStepsEl.getBoundingClientRect();

      if (
        nextStepsEl.scrollHeight >= window.innerHeight &&
        nextStepsDimensions.top <= 0
      ) {
        nextStepsEl.classList.add(
          'summary__next-steps--scrollable-sticky-container'
        );
      } else {
        nextStepsEl.classList.remove(
          'summary__next-steps--scrollable-sticky-container'
        );
      }
    };

    if (nextStepsEl) {
      window.addEventListener('scroll', () => makeNextStepsScrollable());
    }

    return () => {
      if (nextStepsEl) {
        window.removeEventListener('scroll', () => makeNextStepsScrollable());
      }
    };
  }, [nextStepsRef]);

  useEffect(() => {
    const { styleId } = config;
    let isMounted = true;
    if (isMounted) {
      fetchStandardFeatures({ ...modelInfo, styleId }).then((data) => {
        setIsLoadingStandardFeatures(false);
        setStandardFeatures(data);
      });
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const onClose = () => {
    navigate('');
  };

  const { exterior, interior } = config;
  const { cgiImageUrl: interiorCgiImageUrl } = interior;
  const { cgiImageUrl: exteriorCgiImageUrl } = exterior;

  const imageSize = computeImageSize(breakpoint, cgiSize);
  const defaultInteriorAngle = computeDefaultAngle(
    null,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    true
  );
  const defaultExteriorAngle = computeDefaultAngle(
    'default',
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    false
  );

  const interiorCgiUrl = template(interiorCgiImageUrl)({
    imageSize,
    degree: defaultInteriorAngle,
    removeCat: '',
  });

  const exteriorCgiUrl = template(exteriorCgiImageUrl)({
    imageSize,
    degree: defaultExteriorAngle,
    removeCat: '',
  });

  const onSaveClick = currentBuildSavedKey
    ? () => {
        removeBuild(currentBuildSavedKey);
      }
    : () => {
        saveBuild();
        setShowSavedBuildsToast(true);
      };

  const SavedBuildDisclosureContent = ({ closeModal }) => (
    <ToastModalContent closeModal={closeModal}>
      <div className="saved-build-disclosure__container">
        <div className="saved-build-disclosure__label">
          <SvgIcon name="saved" className="button__icon--left" />
          <TranslateHTML as="span" value="saved" />!
        </div>
        <a
          onClick={() => {
            navigate('./savedbuilds');
            closeModal();
          }}
          className="saved-build-disclosure__link">
          <Translate value="saved.builds.toast.link.label.v3" />
        </a>
      </div>
    </ToastModalContent>
  );

  const SavedBuildToast = () => (
    <ToastModal
      useTimeout={false}
      onClose={() => {
        setShowSavedBuildsToast(false);
      }}>
      {({ setIsClosing, closeModal }) => (
        <SavedBuildDisclosureContent
          closeModal={() => setIsClosing(true, closeModal())}
        />
      )}
    </ToastModal>
  );

  const heartIconName = currentBuildSavedKey ? 'heart-active' : 'heart-default';

  const { isPreOrderCompleted } = reservation;

  const SummaryLinks = ({ className }) => (
    <div className={combineClassNames('summary__links', className)}>
      <SummaryButton
        onClick={() => {
          navigate('share');
          saveBuild();
        }}
        iconName="send"
        label={<Translate value="summary.vehicle.send.link.v3" />}
      />
      {!hasTransactionId && (
        <SummaryButton
          onClick={onSaveClick}
          iconName={heartIconName}
          label={
            currentBuildSavedKey ? (
              <Translate value="summary.vehicle.saved.link.v3" />
            ) : (
              <Translate value="summary.vehicle.save.link.v3" />
            )
          }
        />
      )}
      <PrintButton analyticsKey={`${printAnalyticsKey}`} />
    </div>
  );

  const PrintDisclaimers = ({ pricing, disclaimers, country }) => {
    const { incentives, employeePrice } = pricing;
    const creditProgramDisclaimers = incentives.lines.map((line) =>
      line.disclaimers.map((disclaimer, index) => (
        <li
          key={`${line.program}-${index}`}
          className="summary-print__disclaimer"
          dangerouslySetInnerHTML={{
            __html: `${line.program} - ${disclaimer}`,
          }}
        />
      ))
    );
    return (
      <div className="print-footer-disclaimer show-print">
        <ul>
          {employeePrice && country === 'CA' ? (
            <>
              <li className="summary-print__disclaimer">
                <Translate value="byo.disclaimer.total.price" />
              </li>
              <li className="summary-print__disclaimer">
                <Translate value="byo.disclaimer.total.employee.price" />
              </li>
            </>
          ) : (
            <li
              className="summary-print__disclaimer"
              dangerouslySetInnerHTML={{ __html: incentives.disclaimer }}
            />
          )}
          <TranslateHTML
            as="li"
            className="summary-print__disclaimer"
            value="accessories.price.disclaimer"
          />
          <li
            className="summary-print__disclaimer"
            dangerouslySetInnerHTML={{ __html: disclaimers.simple }}
          />
          <li className="summary-print__disclaimer">
            <Translate value="byo.footer.disclaimer" />
          </li>
          <li className="summary-print__disclaimer">
            <Translate value="byo.disclaimer.employee.net.price" />
          </li>
          {creditProgramDisclaimers}
        </ul>
      </div>
    );
  };

  const analyticsKeyLocation = 'right rail';

  const SummaryButton = ({ className, children, iconName, label, onClick }) => (
    <Button
      className={combineClassNames(
        'stat-button-link summary__links__button',
        className
      )}
      data-dtm={isEvMode ? analyticsKeyLocation : `${ANALYTICS_KEY}:top`}
      onClick={onClick}>
      {iconName && (
        <SvgIcon
          name={iconName}
          className="button__icon--left summary-button__icon"
        />
      )}
      <span className="summary__links__button__label">{label}</span>
    </Button>
  );

  const OrderSubmittedText = ({ reservation }) => {
    const { dealer } = reservation;
    return (
      <div className="ev-next-steps">
        <span className="ev-next-steps__submitted">
          <Translate value="ev.next.steps.submitted.v3" />
        </span>
        <span className="ev-next-steps__contact">
          <Translate
            value="ev.next.steps.contact.v3"
            context={{
              dealer: dealer.title,
            }}
          />
          <a
            className="ev-next-steps__contact--phone"
            href={`tel:${dealer.phone}`}>
            {dealer.phone}
          </a>
        </span>
      </div>
    );
  };

  const PublicOrderingLink = () => {
    return (
      <>
        {isEvMode && !hasTransactionId ? (
          <>
            {summaryStep.header && (
              <div className="summary__next-step-header">
                {summaryStep.header}
              </div>
            )}
            <NextStepsLinks
              showAsButton
              ctas={ctas}
              config={config}
              modelInfo={modelInfo}
              postalCode={params.postalCode}
            />
          </>
        ) : null}
      </>
    );
  };

  const [inventoryResult, setInventoryResult] = useState({
    isMatch: false,
    isExactMatch: false,
    vsrUrl: null,
    vdpUrl: null,
  });

  useEffect(() => {
    let isMounted = true;
    fetchInventory(modelInfo, config, params.postalCode).then((inventory) => {
      if (isMounted) {
        setInventoryResult({
          ...inventory,
          isExactMatch: inventory.exactMatches === 1,
          isMatch: inventory.count,
        });
      }
    });
    return () => {
      isMounted = false;
    };
  }, [params.postalCode]);
  return (
    <>
      <GMWCContainer config={config} postalCode={params.postalCode}>
        {(gmwcState) => (
          <div className="summary__container">
            <main className="summary__content-container">
              <div className="summary__images-container">
                <LegalDisclosure className="show-print" showAsStatic={true} />
                <div className="summary__vehicle--exterior">
                  <SummaryExteriorCgi
                    className="summary__vehicle__image-exterior"
                    cgiImageUrl={exteriorCgiUrl}
                    onClick={() => {
                      navigate(`${moreViewsPath?.pathname}?isExterior=true`);
                    }}
                  />
                  <LegalDisclosure className="hide-print" />
                  <MoreViewsButton
                    analyticsKey={`${ANALYTICS_KEY}:top:exterior`}
                    step={EXTERIOR}
                  />
                </div>
                <div className="summary__vehicle--interior">
                  <SummaryInteriorCGI
                    cgiImageUrl={interiorCgiUrl}
                    onClick={() => {
                      navigate(`${moreViewsPath?.pathname}?isExterior=false`);
                    }}
                  />
                  <MoreViewsButton
                    inverted
                    step={INTERIOR}
                    analyticsKey={`${ANALYTICS_KEY}:top:interior`}
                  />
                </div>
              </div>
              <SummaryInner
                className="summary__content-container--inner"
                condition={isAtLeastLarge}>
                <div className="summary__vehicle--description">
                  <TranslateHTML
                    value="summary.vehicle.description.v3"
                    year={modelInfo.year}
                    model={modelInfo.title}
                    trim={config.style.trimName}
                    drive={config.driveType.label}
                  />
                </div>
                <h2>
                  {evStaticDisclosure &&
                    hasFeeDisclaimers &&
                    evGovtIncentive.length > 0 && (
                      <Translate value="byo.summary.disclosure.ev" />
                    )}
                  {'  '}
                  <button
                    className="incentive__disclosure"
                    onClick={(event) =>
                      onOpenDisclosure(
                        event,
                        notifications.show,
                        evGovtIncentiveDisclosure
                      )
                    }>
                    {evStaticDisclosure &&
                      hasFeeDisclaimers &&
                      evGovtIncentive.length > 0 && (
                        <Translate value="byo.summary.disclosuremodal.ev" />
                      )}{' '}
                  </button>
                </h2>
                <NetPriceBox
                  pricing={pricing}
                  notifications={notifications}
                  config={config}
                />
                {((isCanada && !hasTransactionId) || !isCanada) && (
                  <div
                    className={combineClassNames(
                      'summary__payment-options',
                      appResources.display.showPaymentEstimatorComponent &&
                        !pricing.unknownPriceDisplay &&
                        ((paymentEstimatorComponent.finance &&
                          gmwcState.fshowsum) ||
                          (paymentEstimatorComponent.lease &&
                            gmwcState.lshowsum))
                        ? ''
                        : 'summary__payment-options--empty'
                    )}>
                    {appResources.display.showPaymentEstimatorComponent &&
                      !pricing.unknownPriceDisplay && (
                        <SummaryFinanceContent
                          showFinance={
                            paymentEstimatorComponent.finance &&
                            gmwcState.fshowsum
                          }
                          showLease={
                            !(country === 'US' && isEvMode) &&
                            paymentEstimatorComponent.lease &&
                            gmwcState.lshowsum
                          }
                        />
                      )}
                  </div>
                )}
                {!isAtLeastLarge ? (
                  <div className="summary__next-steps--container">
                    <div className="summary__next-steps">
                      {!hasTransactionId && (
                        <Inventory
                          modelInfo={modelInfo}
                          postalCode={params.postalCode}
                          onEditLocation={onEditLocation}
                          inventoryPageUrlTemplate={inventoryPageUrlTemplate}
                          inventoryResult={inventoryResult}
                        />
                      )}
                      <PublicOrderingLink />
                      {!hasTransactionId && !isEvMode && (
                        <NextStepsLinks
                          ctas={ctas}
                          config={config}
                          modelInfo={modelInfo}
                          postalCode={params.postalCode}
                          inventoryResult={inventoryResult}
                        />
                      )}
                      {!hasTransactionId && (
                        <>
                          <SummaryLinks className="summary__links--next-steps" />
                          {privateOrderingMessageLabel && (
                            <div className="summary__hev--message">
                              {privateOrderingMessageLabel}
                            </div>
                          )}
                        </>
                      )}
                      {hasTransactionId &&
                        (isPreOrderCompleted ? (
                          <OrderSubmittedText reservation={reservation} />
                        ) : (
                          <SubmitOrder
                            onSaveClick={onSaveClick}
                            currentBuildSavedKey={currentBuildSavedKey}
                            modelInfo={modelInfo}
                            summaryStep={summaryStep}
                          />
                        ))}
                      {hasTransactionId && (
                        <SummaryLinks
                          className="summary__links--next-steps"
                          analyticsKey={analyticsKeyLocation}
                        />
                      )}
                    </div>
                    <FeaturedContent modelInfo={modelInfo} />
                    <div className="summary__chat">
                      <Chat alt analyticsKey={analyticsKeyLocation} />
                    </div>
                  </div>
                ) : null}
                <div className="summary__price-details">
                  <MathBox
                    pricing={pricing}
                    config={config}
                    modelInfo={modelInfo}
                    analyticsKey={ANALYTICS_KEY}
                  />
                </div>
                <FeaturedContent
                  type="subscription"
                  className="featured-content-container--subscription"
                  modelInfo={modelInfo}
                />
                <KeyAttributes config={config} />
                <div className="summary__standard-features">
                  <h2 className="summary__standard-features-header">
                    {standardEquipmentTitle}{' '}
                    <Translate value="standard.equipment.v3" />
                  </h2>
                  {isLoadingStandardFeatures ? (
                    <TranslateHTML
                      as="div"
                      value="loading.standard.equipment.v3"
                    />
                  ) : (
                    <>
                      <StandardFeatures
                        features={standardFeatures}
                        analyticsKey="standard features"
                        className={combineClassNames(
                          'standard-features__accordion--summary',
                          'hide-print'
                        )}
                      />
                      <StandardFeatures
                        features={standardFeatures}
                        analyticsKey="standard features"
                        className={combineClassNames(
                          'standard-features__accordion--summary',
                          'show-print'
                        )}
                        isOpenDefault={true}
                      />
                    </>
                  )}
                </div>
              </SummaryInner>
              {isAtLeastLarge ? (
                <div
                  className="summary__next-steps--container"
                  ref={nextStepsRef}>
                  <div className="summary__next-steps">
                    {!hasTransactionId && (
                      <Inventory
                        modelInfo={modelInfo}
                        postalCode={params.postalCode}
                        onEditLocation={onEditLocation}
                        inventoryPageUrlTemplate={inventoryPageUrlTemplate}
                        inventoryResult={inventoryResult}
                      />
                    )}
                    <PublicOrderingLink />
                    {!hasTransactionId && !isEvMode && (
                      <NextStepsLinks
                        ctas={ctas}
                        config={config}
                        modelInfo={modelInfo}
                        postalCode={params.postalCode}
                        inventoryResult={inventoryResult}
                      />
                    )}
                    {!hasTransactionId && (
                      <>
                        <SummaryLinks className="summary__links--next-steps" />
                        {privateOrderingMessageLabel && (
                          <div className="summary__hev--message">
                            {privateOrderingMessageLabel}
                          </div>
                        )}
                      </>
                    )}
                    {hasTransactionId &&
                      (isPreOrderCompleted ? (
                        <OrderSubmittedText reservation={reservation} />
                      ) : (
                        <SubmitOrder
                          onSaveClick={onSaveClick}
                          currentBuildSavedKey={currentBuildSavedKey}
                          modelInfo={modelInfo}
                          summaryStep={summaryStep}
                        />
                      ))}
                    {hasTransactionId && (
                      <SummaryLinks className="summary__links--next-steps" />
                    )}
                  </div>
                  <FeaturedContent modelInfo={modelInfo} />
                  <div className="summary__chat">
                    <Chat alt analyticsKey={analyticsKeyLocation} />
                  </div>
                </div>
              ) : null}
              <PrintDisclaimers
                pricing={pricing}
                disclaimers={disclaimers}
                country={country}
              />
            </main>
            <div className="summary__footer-section">
              <Footer className="summary__footer" />
            </div>
            {showSavedBuildsToast && <SavedBuildToast />}
            <Routes>
              <Route
                path="/payment"
                element={
                  <PaymentEstimatorModal
                    bodyStyleTitle={modelInfo.title}
                    params={params}
                    config={config}
                    defaultTabIndex={
                      getQueryStringValue(location.search, 'initialTab') ===
                      'lease'
                        ? 1
                        : 0
                    }
                    onClose={() => {
                      navigate('');
                    }}
                    urls={appResources.urls}
                    ctas={ctas}
                  />
                }
              />
              <Route
                path="/share"
                element={
                  <SendLink
                    onClose={onClose}
                    config={config}
                    postalCode={params.postalCode}
                    modelInfo={modelInfo}
                  />
                }
              />
            </Routes>
          </div>
        )}
      </GMWCContainer>
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    appResources,
    config,
    isLoadingStandardFeatures,
    isSelectionRevealed,
    params,
    rpos,
    standardFeatures,
    viewInventoryUrlTemplate,
    breakpoint,
    disclaimers,
    reservation,
  } = state;

  const {
    display: { cgiBackgroundColor },
    cgiSize,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    country,
    vsEmbeddedUrl,
    vsEmbedded,
  } = appResources;

  const inventoryPageUrlTemplate = vsEmbedded
    ? vsEmbeddedUrl
    : viewInventoryUrlTemplate;

  return {
    config,
    isLoadingStandardFeatures,
    isSelectionRevealed,
    params,
    rpos,
    standardFeatures,
    cgiBackgroundColor,
    inventoryPageUrlTemplate,
    breakpoint,
    cgiSize,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    disclaimers,
    country,
    reservation,
  };
};

const mapDispatchToProps = {
  onLoad: () => setSelectionRevealState(),
};

export default withSaveBuild(
  withNotifications(connect(mapStateToProps, mapDispatchToProps)(Summary))
);
